import { useQuery } from '@tanstack/react-query';
import { locationService } from '@/services/location.service';
import { LocationSummaryDto } from '@/types/location';
import { BillingCycle, Plan, PlanStatus } from '@/types/plan';

/**
 * The two plans sold as Founding Memberships for a location:
 *   - "Performance"        — 1-year contract, paid monthly
 *   - "Performance Annual" — 1-year contract, paid annually
 * Matched by name (case-insensitive, trimmed), as agreed with product.
 */
export interface FoundingPlans {
  monthly?: Plan;
  annual?: Plan;
}

/** Locations the signed-in user is scoped to (drives the playbook location picker). */
export function useSwitcherLocations() {
  return useQuery<LocationSummaryDto[]>({
    queryKey: ['locations', 'switcher'],
    queryFn: () => locationService.getSwitcherLocations(),
  });
}

/** Plans for a single location. Idle until a locationId is known. */
export function useLocationPlans(locationId?: string) {
  return useQuery<Plan[]>({
    queryKey: ['location', locationId, 'plans'],
    queryFn: () => locationService.getLocationPlans(locationId as string),
    enabled: Boolean(locationId),
  });
}

/**
 * Default location for the playbook pricing:
 *   - a single-location (scoped) user → their one location;
 *   - a multi/global user → "Centerville" by name, falling back to the first.
 */
export function resolveDefaultLocationId(
  locations: LocationSummaryDto[] | undefined,
): string | undefined {
  if (!locations?.length) return undefined;
  if (locations.length === 1) return locations[0].id;
  const centerville = locations.find((l) => /centerville/i.test(l.name));
  return (centerville ?? locations[0]).id;
}

/** Pick the two Founding Membership plans out of a location's plans, by name. */
export function pickFoundingPlans(plans: Plan[] | undefined): FoundingPlans {
  if (!plans?.length) return {};
  const active = plans.filter((p) => p.status === PlanStatus.ACTIVE);
  const pool = active.length ? active : plans;
  const named = (name: string) =>
    pool.find((p) => p.name?.trim().toLowerCase() === name);
  const monthly =
    named('performance') ??
    pool.find(
      (p) =>
        /^performance$/i.test(p.name?.trim() ?? '') &&
        p.billingCycle === BillingCycle.MONTHLY,
    );
  const annual = named('performance annual');
  return { monthly, annual };
}
